// Component: BacktestConfigPanel.jsx
// Purpose: Backtest configuration (dates, capital, slippage, F&O settings, broker). 144-6 R5: the commission % input is
//          DELETED - brokerage + statutory charges come from the managed schedule of the selected broker, which this
//          panel lists from GET /backtests/brokers (any user) and sends as the run's `broker`.

import React, { useEffect, useState } from 'react';
import { services } from '../../../../../services/api';

//--> The API's managed broker list: [{key, label, verification_status}] (non-reference, deduped by the server).
const readBrokers = (response) => {
    const body = response?.data;
    const data = body && typeof body === 'object' && 'data' in body ? body.data : body;
    return Array.isArray(data?.brokers) ? data.brokers.filter((b) => b && b.key) : [];
};

const BacktestConfigPanel = ({ config, setConfig, dateRange }) => {
    //--> Managed-schedule brokers for every user; an unreadable list leaves only the account default.
    const [brokers, setBrokers] = useState([]);
    useEffect(() => {
        let alive = true;
        services.backtests.listBrokers()
            .then((response) => { if (alive) setBrokers(readBrokers(response)); })
            .catch(() => { if (alive) setBrokers([]); });
        return () => { alive = false; };
    }, []);

    //--> Pre-fill dates once from selected strategy timeline if user has not set them.
    useEffect(() => {
        if (dateRange?.start && !config.startDate) {
            setConfig(prev => ({ ...prev, startDate: dateRange.start }));
        }
        if (!config.endDate) {
            if (dateRange?.end) {
                setConfig(prev => ({ ...prev, endDate: dateRange.end }));
            } else {
                setConfig(prev => ({ ...prev, endDate: new Date().toISOString().split('T')[0] }));
            }
        }
    }, [dateRange, config.startDate, config.endDate, setConfig]);

    return (
        <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] p-5 flex flex-col h-full">
            <h3 className="text-sm font-extrabold text-fg-1 uppercase tracking-wider mb-4 border-b border-[var(--divider)] pb-3">
                Configuration
            </h3>

            <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                {/* Dates */}
                <div>
                    <label className="block text-xs font-bold text-fg-2 uppercase mb-2">Start Date</label>
                    <input 
                        type="date" 
                        value={config.startDate || ''}
                        onChange={(e) => setConfig({ ...config, startDate: e.target.value })}
                        className="ds-input w-full"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-fg-2 uppercase mb-2">End Date</label>
                    <input 
                        type="date" 
                        value={config.endDate || ''}
                        onChange={(e) => setConfig({ ...config, endDate: e.target.value })}
                        className="ds-input w-full"
                    />
                </div>

                {/* Capital */}
                <div>
                    <label className="block text-xs font-bold text-fg-2 uppercase mb-2">Initial Capital (₹)</label>
                    <input 
                        type="number" 
                        value={config.initialCapital || 100000}
                        onChange={(e) => setConfig({ ...config, initialCapital: Number(e.target.value) })}
                        className="ds-input w-full"
                    />
                </div>

                {/* Costs - 144-6: no commission input; charges come from the selected broker's managed schedule */}
                <div>
                    <label htmlFor="bt-broker" className="block text-[10px] font-bold text-fg-2 uppercase mb-1">Broker</label>
                    <select
                        id="bt-broker"
                        value={config.broker || ''}
                        onChange={(e) => setConfig({ ...config, broker: e.target.value })}
                        className="ds-input w-full"
                    >
                        <option value="">Account default (primary broker, else Flattrade)</option>
                        {brokers.map((broker) => (
                            <option key={broker.key} value={broker.key}>{broker.label || broker.key}</option>
                        ))}
                    </select>
                    <p className="text-[9px] text-fg-2 italic mt-1">
                        Brokerage and statutory charges come from the live schedule for this broker.
                    </p>
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-fg-2 uppercase mb-1">Slippage %</label>
                    <input 
                        type="number" 
                        step="0.01"
                        value={config.slippage || 0.05}
                        onChange={(e) => setConfig({ ...config, slippage: Number(e.target.value) })}
                        className="ds-input w-full"
                    />
                </div>

                {/* F&O Lot Size Settings */}
                <div className="pt-3 border-t border-[var(--divider)] space-y-3">
                    <h4 className="text-[10px] font-bold text-fg-2 uppercase tracking-wide">F&O Settings</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-bold text-fg-2 uppercase mb-1">Lots per Signal</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={config.numLots || 1}
                                onChange={(e) => setConfig({ ...config, numLots: Number(e.target.value) || 1 })}
                                className="ds-input w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-fg-2 uppercase mb-1">Margin %</label>
                            <input
                                type="number"
                                min="0.1"
                                step="0.5"
                                value={config.marginPct || 12.0}
                                onChange={(e) => setConfig({ ...config, marginPct: Number(e.target.value) || 12.0 })}
                                className="ds-input w-full"
                            />
                        </div>
                    </div>
                    <p className="text-[9px] text-fg-2 italic">
                        💡 Margin % is the capital required (e.g., 12% for futures, 100% for equity)
                    </p>
                </div>

                {/* Recalculation Controls */}
                <div className="pt-2 border-t border-[var(--divider)] space-y-3">
                    <label className="flex items-center gap-2 text-xs font-semibold text-fg-2">
                        <input
                            type="checkbox"
                            checked={!!config.forceRecalculate}
                            onChange={(e) => setConfig({ ...config, forceRecalculate: e.target.checked })}
                            className="rounded border-[var(--divider)] text-blue-600 focus:ring-blue-500"
                        />
                        Force Recalculation (bypass cache)
                    </label>

                    <div>
                        <label className="block text-[10px] font-bold text-fg-2 uppercase mb-1">Recalculate From (Optional)</label>
                        <input
                            type="datetime-local"
                            value={config.recalcFromTimestamp || ''}
                            onChange={(e) => setConfig({ ...config, recalcFromTimestamp: e.target.value })}
                            className="ds-input w-full"
                        />
                    </div>

                </div>
            </div>

        </div>
    );
};

export default BacktestConfigPanel;
